import type { Customer } from '../../gads'

import { Core } from '../reports'
import { Analytics } from '../analytics/analytics'
import { SlideEnums } from './enums'
import { Targets } from '../..'

export namespace Slides {
    // Data available to use in all slide & insights copy building
    export interface BuildSlideCopyOptions {
        [name: string]: any
        gads: Customer // Gads instance for current domain id, ready for queries
        user_id: number
        domain_id: number
        last_period_from_date: string // "2018-12-01"
        from_date: string // "2019-03-01"
        to_date: string // "2019-05-01"
        date_range: string // "March 1st - May 1st"
        prev_date_range: string // "December 1st - February 28th"
        metrics: Array<Core.CopyMetrics>
        currency: string // "£"|"$"|"GBP"
        spend_target: Core.Target
        cpa_target: Core.Target
        roas_target: Core.Target
        ads?: Array<Core.TopPerformingAd>
        keywords?: Array<{
            resource_name: string
            text: string
            clicks: number
            conversions: number
            conversion_rate: number
            impressions: number
            cpc: number
        }>
        highest_keyword_cpc?: {
            text: string
            cpc: number
        }
        segments: {
            best_segments?: Segment[]
            worst_segments?: Segment[]
        }
        improvements: FeaturedImprovements
        counts: BuildSlideCounts
        title: string // Title of report set by user
        dates: Core.ReportDates
        alerts: Core.ReportAlerts | undefined
        top_non_branding_keyword: Core.TopNonBrandingKeyword | undefined
        sender: Core.Sender
        analytics?: Analytics.Slide
        performance_mode: Targets.PerformanceMode
    }

    export type BuildSlideCounts = { [type: string]: number } & {
        all_improvements: BuildSlideAllImprovementsCounts
    }

    export interface BuildSlideAllImprovementsCounts {
        [rec_action: string]: {
            count: number
            type: string
            rec_action: string
            completed_timestamp: string
        }
    }

    export interface Segment {
        target_segment: string
        segment_label: string
        real_name: string
        conversions: number
        conversions_value: number
        cost: number
        cpa: number
        roas: number
        difference: number
    }

    export interface FeaturedImprovements {
        adjust_keyword_bid: FeaturedImprovement
        pause_ad: FeaturedImprovement
        write_ad: FeaturedImprovement
        adjust_age_bid: FeaturedImprovement
        adjust_gender_bid: FeaturedImprovement
        adjust_parental_bid: FeaturedImprovement
        adjust_ad_schedule_bids: FeaturedImprovement
        adjust_geo_bids: Array<FeaturedImprovement>
    }

    export interface FeaturedImprovement {
        completed_improvement_id: number
        improvement_id: number
        domain_id: number
        completed_timestamp: string
        type: string
        body: any
        pushed_data: any
    }

    // Ensure that this matches the keys of the SlideType enum
    export type SlideTypes =
        | 'Cover'
        | 'ReportSummary'
        | 'GoogleAnalyticsSummary'
        | 'SpendSummary'
        | 'ConversionsSummary'
        | 'ConversionsValueSummary'
        | 'CostPerConversionSummary'
        | 'ReturnOnAdSpendSummary'
        | 'ImpressionsSummary'
        | 'ClicksSummary'
        | 'CostPerClickSummary'
        | 'SpendTargets'
        | 'CpaTargets'
        | 'RoasTargets'
        | 'TopPerformingAds'
        | 'TopPerformingKeywords'
        | 'TopPerformingSegments'
        | 'AdTest'
        | 'AdCreative'
        | 'FeaturedKeywordBid'
        | 'FeaturedScheduleAdjustment'
        | 'FeaturedAgeBid'
        | 'FeaturedGenderBid'
        | 'FeaturedParentalStatusBid'
        | 'FeaturedLocationBid'
        | 'WorkSummary'
        | 'NextSteps'
        | 'Conclusion'

    export interface SlideTypeToMetric {
        [type: number]: string
    }

    export const SlideTypeToMetric: SlideTypeToMetric = {
        [SlideEnums.SlideType.SpendSummary]: 'cost',
        [SlideEnums.SlideType.ConversionsSummary]: 'conversions',
        [SlideEnums.SlideType.ConversionsValueSummary]: 'conversions_value',
        [SlideEnums.SlideType.CostPerConversionSummary]: 'cpa',
        [SlideEnums.SlideType.ReturnOnAdSpendSummary]: 'roas',
        [SlideEnums.SlideType.ImpressionsSummary]: 'impressions',
        [SlideEnums.SlideType.ClicksSummary]: 'clicks',
        [SlideEnums.SlideType.CostPerClickSummary]: 'cpc',
    }

    export interface SlideTypeCategory {
        slide_category: SlideEnums.SlideCategory
        slide_types: SlideEnums.SlideType[]
        category_name: string
    }
}
